import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '@/components/layout'
import {
  Button,
  TextArea,
  Card,
  Spinner,
  ToastContainer,
  useToast,
  Toggle,
  Tabs,
  Badge,
  Input,
  Tooltip,
  RadioGroup,
} from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import { useSubscription } from '@/context/SubscriptionContext'
import { useAccounts } from '@/context/AccountContext'
import { useGenerationJob } from '@/hooks/useGenerationJob'
import { PaywallModal } from '@/components/PaywallModal'
import { UpgradeBanner } from '@/components/UpgradeBanner'
import { AccountSelector } from '@/components/AccountSelector'
import type { ContentType, ToneOverride, ContextType } from '@/services/types'
import { ErrorCode } from '@/services/types'
import { getContentTypesForPlatform } from '@/services/platforms'

const CONTENT_TYPE_LABELS: Record<string, string> = {
  single_tweet: 'Single Post',
  thread: 'Thread',
  quote_reply: 'Quote Reply Format',
  instagram_caption: 'Caption',
}

const ALL_CONTENT_TYPE_OPTIONS = [
  { value: 'single_tweet', label: 'Single Post' },
  { value: 'thread', label: 'Thread' },
  { value: 'quote_reply', label: 'Quote Reply Format' },
  { value: 'instagram_caption', label: 'Caption' },
]

const TONE_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'professional', label: 'Professional' },
  { value: 'humorous', label: 'Humorous' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'educational', label: 'Educational' },
  { value: 'provocative', label: 'Provocative' },
]

const CONTEXT_TABS = [
  { id: 'url', label: 'URL' },
  { id: 'text', label: 'Text' },
]

function InfoIcon() {
  return (
    <svg
      className="w-4 h-4 text-secondary-text"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

export function CreatePage() {
  const navigate = useNavigate()
  const { toasts, addToast, dismissToast } = useToast()
  const { usage, refreshUsage } = useSubscription()
  const { selectedAccount } = useAccounts()
  const { status, isGenerating: jobIsGenerating, errorMessage, startJob, clearJob } = useGenerationJob()

  // Paywall state
  const [showPaywall, setShowPaywall] = useState(false)

  // Prompt state
  const [prompt, setPrompt] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Context state
  const [contextEnabled, setContextEnabled] = useState(true)
  const [contextType, setContextType] = useState<ContextType>('url')
  const [contextValue, setContextValue] = useState('')

  // Content type & tone state
  const [contentType, setContentType] = useState<ContentType>('single_tweet')
  const [toneOverride, setToneOverride] = useState<ToneOverride | null>(null)
  const [toneEnabled, setToneEnabled] = useState(true)

  // Build platform-specific content type options
  const platform = selectedAccount?.platform ?? 'twitter'
  const supportedTypes = getContentTypesForPlatform(platform)
  const contentTypeOptions = ALL_CONTENT_TYPE_OPTIONS.filter((o) =>
    supportedTypes.includes(o.value as ContentType)
  )

  // Reset content type when switching accounts if current type is invalid
  useEffect(() => {
    if (!supportedTypes.includes(contentType)) {
      setContentType(supportedTypes[0])
    }
  }, [platform]) // eslint-disable-line react-hooks/exhaustive-deps

  // Show error toast when job fails
  useEffect(() => {
    if (status === 'failed') {
      addToast(errorMessage || 'Content generation failed', 'error')
      clearJob()
    }
  }, [status, errorMessage, addToast, clearJob])

  // Refresh usage when job completes
  useEffect(() => {
    if (status === 'completed') {
      refreshUsage()
    }
  }, [status, refreshUsage])

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      addToast('Please enter a prompt', 'error')
      return
    }

    // Always fetch fresh usage before checking
    const freshUsage = await refreshUsage()
    const remaining = typeof freshUsage?.generations_remaining === 'number'
      ? freshUsage.generations_remaining
      : (freshUsage ? freshUsage.generations_limit - freshUsage.generations_used : 0)
    const canGenerateNow = freshUsage ? freshUsage.is_unlimited || remaining > 0 : true

    if (!canGenerateNow) {
      setShowPaywall(true)
      return
    }

    setIsSubmitting(true)

    const useVoice = selectedAccount ? selectedAccount.voice_config.mode !== 'off' : false

    try {
      const response = await api.generateContent(prompt, 1, {
        useVoice: useVoice || undefined,
        contentType,
        toneOverride: toneEnabled ? toneOverride ?? undefined : undefined,
        context: contextEnabled && contextValue.trim()
          ? { type: contextType, value: contextValue.trim() }
          : undefined,
        accountId: selectedAccount?.id,
      })
      startJob(response.job_id)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === ErrorCode.GENERATION_LIMIT_REACHED) {
          setShowPaywall(true)
          await refreshUsage()
        } else {
          addToast(getErrorMessage(err, 'Failed to generate content'), 'error')
        }
      } else {
        addToast('Failed to generate content', 'error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Build active options badges for prompt card
  const activeOptions: string[] = []
  if (selectedAccount && selectedAccount.voice_config.mode === 'automated') activeOptions.push('Voice')
  else if (selectedAccount && selectedAccount.voice_config.mode === 'manual') activeOptions.push('Manual Voice')
  if (contentType !== 'single_tweet') {
    activeOptions.push(CONTENT_TYPE_LABELS[contentType] || contentType)
  }
  if (toneEnabled && toneOverride) {
    activeOptions.push(TONE_OPTIONS.find(o => o.value === toneOverride)?.label || toneOverride)
  }
  if (contextEnabled && contextValue.trim()) activeOptions.push('Context')

  const generateDisabled = !prompt.trim() || isSubmitting || jobIsGenerating

  return (
    <PageContainer title="Create Content">
      <div className="space-y-6">
        {usage && usage.plan === 'free' && usage.generations_remaining > 0 && usage.generations_remaining < 10 && (
          <UpgradeBanner remaining={usage.generations_remaining} />
        )}

        {/* Account Selector */}
        <AccountSelector />

        {/* Prompt Card */}
        <Card>
          <h2 className="text-lg font-medium mb-4">Generate New Posts</h2>

          {activeOptions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {activeOptions.map((option) => (
                <Badge key={option}>{option}</Badge>
              ))}
            </div>
          )}

          <div className="space-y-4">
            <TextArea
              label="Prompt"
              placeholder="What do you want to write about? e.g., 'Share thoughts on AI and productivity' or 'Write about the importance of code reviews'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
            />

            <div className="flex items-center gap-4">
              <Button
                onClick={handleGenerate}
                loading={isSubmitting || jobIsGenerating}
                disabled={generateDisabled}
              >
                {jobIsGenerating ? 'Generating...' : 'Generate'}
              </Button>

              {jobIsGenerating && (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Spinner size="sm" />
                  <p className="text-sm text-secondary-text truncate">
                    Your content is being generated...
                  </p>
                </div>
              )}

              {status === 'completed' && (
                <>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm font-semibold truncate">
                      Your content is ready
                    </p>
                  </div>
                  <button
                    onClick={() => { clearJob(); navigate('/queue'); }}
                    className="px-4 py-2 font-medium bg-success text-white hover:bg-green-700 transition-colors animate-glow-pulse focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2"
                  >
                    View Content
                  </button>
                </>
              )}
            </div>

            {usage && usage.plan === 'free' && usage.generations_remaining > 0 && (
              <p className={`text-xs ${
                usage.generations_remaining <= 5
                  ? 'text-red-600'
                  : usage.generations_remaining < 10
                    ? 'text-amber-600'
                    : 'text-secondary-text'
              }`}>
                {usage.generations_remaining} of {usage.generations_limit} free generations remaining
              </p>
            )}
          </div>
        </Card>

        {/* Context/Response Card */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium">Response Context</h2>
              <Tooltip content="Generate content in response to existing content">
                <InfoIcon />
              </Tooltip>
            </div>
            <Toggle
              checked={contextEnabled}
              onChange={(e) => setContextEnabled(e.target.checked)}
              label="Enable"
            />
          </div>

          {contextEnabled && (
            <div className="space-y-4">
              <Tabs
                tabs={CONTEXT_TABS}
                activeTab={contextType}
                onTabChange={(id) => setContextType(id as ContextType)}
              />
              {contextType === 'url' ? (
                <Input
                  placeholder="https://x.com/user/status/..."
                  value={contextValue}
                  onChange={(e) => setContextValue(e.target.value)}
                />
              ) : (
                <TextArea
                  placeholder="Paste the content you want to respond to..."
                  value={contextValue}
                  onChange={(e) => setContextValue(e.target.value)}
                  rows={3}
                />
              )}
            </div>
          )}
        </Card>

        {/* Content Type Card */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-medium">Content Type</h2>
            <Tooltip content="Choose the format for your generated content">
              <InfoIcon />
            </Tooltip>
          </div>
          <RadioGroup
            name="content-type"
            options={contentTypeOptions}
            value={contentType}
            onChange={(value) => setContentType(value as ContentType)}
          />
        </Card>

        {/* Tone Override Card */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium">Tone Override</h2>
              <Tooltip content="Adjust the tone independently of your voice profile">
                <InfoIcon />
              </Tooltip>
            </div>
            <Toggle
              checked={toneEnabled}
              onChange={(e) => setToneEnabled(e.target.checked)}
              label="Adjust tone"
            />
          </div>

          {toneEnabled && (
            <div className="space-y-3">
              <RadioGroup
                name="tone-override"
                options={TONE_OPTIONS}
                value={toneOverride}
                onChange={(value) => setToneOverride(value as ToneOverride)}
              />
              <p className="text-xs text-secondary-text">
                This will override or blend with your voice profile tone
              </p>
            </div>
          )}
        </Card>

      </div>

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
    </PageContainer>
  )
}
